/** Code examples shown on the docs page. Content only — no layout. */

export const IDENTIFY_URL = 'https://api.acceptlens.com/v1/identify';
export const JOBS_URL = 'https://api.acceptlens.com/v1/jobs';
export const TOKEN_URL = 'https://dev-rmiw5hh1oyorjysf.us.auth0.com/oauth/token';
export const AUDIENCE = 'https://api.acceptlens.com';
export const MCP_URL = 'https://api.acceptlens.com/mcp';

type Snippet = {
  label: string;
  language: 'bash' | 'json' | 'javascript' | 'python';
  code: string;
};

export const REQUEST_EXAMPLES: readonly Snippet[] = [
  {
    label: 'cURL',
    language: 'bash',
    code: `# 1. Exchange your OAuth client for an access token (valid 1 hour)
TOKEN=$(curl -s -X POST '${TOKEN_URL}' \\
  -H 'content-type: application/json' \\
  -d '{ "grant_type": "client_credentials",
        "client_id": "<client_id>",
        "client_secret": "<client_secret>",
        "audience": "${AUDIENCE}" }' | jq -r .access_token)

# 2. Submit the store — 200 with the result if one is already available, else 202 with a job_id
curl -X POST '${IDENTIFY_URL}' \\
  -H "Authorization: Bearer $TOKEN" \\
  -H 'content-type: application/json' \\
  -d '{ "url": "https://example.com" }'

# 3. Collect the result — repeat every few seconds while status is queued/running
curl '${JOBS_URL}/<job_id>' -H "Authorization: Bearer $TOKEN"`,
  },
  {
    label: 'JavaScript (fetch)',
    language: 'javascript',
    code: `const token = await getAccessToken(); // client_credentials, see cURL tab

const submitted = await fetch('${IDENTIFY_URL}', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${token}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ url: 'https://example.com' }),
}).then((r) => r.json());

// A result may already be available. Otherwise poll the job.
let result = submitted.result;
while (!result) {
  await new Promise((r) => setTimeout(r, 3000));
  const job = await fetch(\`${JOBS_URL}/\${submitted.job_id}\`, {
    headers: { Authorization: \`Bearer \${token}\` },
  }).then((r) => r.json());
  if (job.status === 'failed') throw new Error(job.message ?? job.error);
  result = job.result;
}

console.log(result.status, result.payments, result.bnpl_advertised);`,
  },
  {
    label: 'Node.js',
    language: 'javascript',
    code: `// Node 18+ — native fetch, no dependencies
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function getAccessToken() {
  const res = await fetch('${TOKEN_URL}', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'client_credentials',
      client_id: process.env.ACCEPTLENS_CLIENT_ID,
      client_secret: process.env.ACCEPTLENS_CLIENT_SECRET,
      audience: '${AUDIENCE}',
    }),
  });
  if (!res.ok) throw new Error(\`token request failed: HTTP \${res.status}\`);
  return (await res.json()).access_token;
}

async function detect(url, token) {
  const res = await fetch('${IDENTIFY_URL}', {
    method: 'POST',
    headers: { Authorization: \`Bearer \${token}\`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) throw new Error(\`submit failed: HTTP \${res.status}\`);
  const submitted = await res.json();
  if (submitted.result) return submitted.result; // already available

  for (let i = 0; i < 80; i++) {
    await sleep(3000);
    const job = await fetch(\`${JOBS_URL}/\${submitted.job_id}\`, {
      headers: { Authorization: \`Bearer \${token}\` },
    }).then((r) => r.json());
    if (job.status === 'succeeded') return job.result;
    if (job.status === 'failed') throw new Error(\`\${job.error}: \${job.message}\`);
  }
  throw new Error('scan did not finish in time');
}

const result = await detect('https://example.com', await getAccessToken());
console.log(result.status, result.payments, result.bnpl_advertised);`,
  },
  {
    label: 'Python',
    language: 'python',
    code: `import os
import time
import requests

token = requests.post(
    "${TOKEN_URL}",
    json={
        "grant_type": "client_credentials",
        "client_id": os.environ["ACCEPTLENS_CLIENT_ID"],
        "client_secret": os.environ["ACCEPTLENS_CLIENT_SECRET"],
        "audience": "${AUDIENCE}",
    },
    timeout=30,
).json()["access_token"]

auth = {"Authorization": f"Bearer {token}"}

submitted = requests.post(
    "${IDENTIFY_URL}",
    headers=auth,
    json={"url": "https://example.com"},
    timeout=30,
).json()

result = submitted.get("result")  # present when already available
while result is None:
    time.sleep(3)
    job = requests.get(
        f"${JOBS_URL}/{submitted['job_id']}", headers=auth, timeout=30
    ).json()
    if job["status"] == "failed":
        raise RuntimeError(f"{job['error']}: {job['message']}")
    result = job.get("result")

print(result["status"], result["payments"], result["bnpl_advertised"])`,
  },
];

export const REQUEST_BODY = `{
  "url": "https://example.com"
}`;

export const POST_RESPONSES = `// 200 — a result is already available
{ "job_id": null, "status": "succeeded", "result": { … } }

// 202 — a scan was queued; poll status_url
{ "job_id": "3f0d6a2e-…", "status": "queued",
  "created_at": "2026-08-20T08:14:22.103Z",
  "status_url": "/v1/jobs/3f0d6a2e-…" }`;

export const JOB_RESPONSES = `// still working — check again in a few seconds
{ "job_id": "3f0d6a2e-…", "status": "running", "elapsed_s": 42 }

// done
{ "job_id": "3f0d6a2e-…", "status": "succeeded",
  "finished_at": "2026-08-20T08:15:31.940Z", "result": { … } }

// failed — both error codes are retryable
{ "job_id": "3f0d6a2e-…", "status": "failed", "error": "stale_job",
  "message": "The worker handling this job stopped responding; submit the URL again" }`;

export const MCP_INSTALL: readonly Snippet[] = [
  {
    label: 'Claude Code',
    language: 'bash',
    code: `# OAuth — after adding, run /mcp inside Claude Code and pick
# "Authenticate" to complete the browser sign-in
claude mcp add --transport http acceptlens ${MCP_URL}`,
  },
  {
    label: 'claude.ai / Desktop',
    language: 'bash',
    code: `# Settings → Connectors → Add custom connector
#   URL: ${MCP_URL}
#
# Leave the advanced OAuth fields empty — the connector discovers the
# sign-in flow from the server on its own.`,
  },
  {
    label: 'ChatGPT',
    language: 'bash',
    code: `# Settings → Connectors → Advanced → Developer mode,
# then Create → MCP server
#   URL:            ${MCP_URL}
#   Authentication: OAuth
#
# The connector registers itself and opens the sign-in flow.
# Custom connectors require a ChatGPT Plus/Pro or Business plan.`,
  },
  {
    label: 'Codex CLI',
    language: 'bash',
    code: `# ~/.codex/config.toml — Codex needs a static token: mint
# ACCEPTLENS_TOKEN first (Quick start, step 1 of the cURL tab) and
# export it before starting Codex. Tokens last one hour.
[mcp_servers.acceptlens]
url = "${MCP_URL}"
bearer_token_env_var = "ACCEPTLENS_TOKEN"`,
  },
  {
    label: 'Cursor',
    language: 'javascript',
    code: `// ~/.cursor/mcp.json — no "headers" block: Cursor runs the
// OAuth sign-in flow itself
{
  "mcpServers": {
    "acceptlens": { "url": "${MCP_URL}" }
  }
}`,
  },
  {
    label: 'VS Code',
    language: 'javascript',
    code: `// .vscode/mcp.json — note "servers", not "mcpServers"; with no
// "headers" block VS Code runs the OAuth sign-in flow itself
{
  "servers": {
    "acceptlens": { "type": "http", "url": "${MCP_URL}" }
  }
}`,
  },
  {
    label: 'Gemini CLI',
    language: 'javascript',
    code: `// ~/.gemini/settings.json — "httpUrl", not "url" (plain "url"
// means SSE). Gemini needs a static token: mint ACCEPTLENS_TOKEN
// first (Quick start, step 1 of the cURL tab).
{
  "mcpServers": {
    "acceptlens": {
      "httpUrl": "${MCP_URL}",
      "headers": { "Authorization": "Bearer \${ACCEPTLENS_TOKEN}" }
    }
  }
}`,
  },
  {
    label: 'opencode',
    language: 'javascript',
    code: `// .opencode/opencode.json (project) or ~/.config/opencode/opencode.jsonc
// Static token: mint ACCEPTLENS_TOKEN first (Quick start, cURL tab, step 1).
{
  "$schema": "https://opencode.ai/config.json",
  "mcp": {
    "acceptlens": {
      "type": "remote",
      "url": "${MCP_URL}",
      "headers": { "Authorization": "Bearer {env:ACCEPTLENS_TOKEN}" },
      "enabled": true
    }
  }
}`,
  },
];
